export type NavbarAuthAction = "login" | "sign-up";

export type NavbarPageContext =
  | "skilled-workers"
  | "laborpass"
  | "tools-financing"
  | "community-manager"
  | "businesses"
  | "homeowners"
  | "unknown";

export type NavbarResolvedDestination = {
  action: NavbarAuthAction;
  pageContext: NavbarPageContext;
  href: string;
  isExternalAppDestination: boolean;
};

type NavbarRouteContext = {
  aliases: string[];
  canonicalPath: string;
  destinationEnvVar:
    | "REACT_APP_HOMEOWNER_DASHBOARD_URL"
    | "REACT_APP_MARKETPLACE_URL"
    | "REACT_APP_PRO_APPLICATION_URL"
    | "REACT_APP_TRAINING_URL";
  pageContext: Exclude<NavbarPageContext, "unknown">;
};

const WEBSITE_AUTH_FALLBACK: Record<NavbarAuthAction, string> = {
  login: "/auth/login",
  "sign-up": "/auth/register",
};

const NAVBAR_AUTH_ROUTE_CONTEXT: NavbarRouteContext[] = [
  {
    canonicalPath: "/skilled-workers",
    aliases: ["/artisans"],
    pageContext: "skilled-workers",
    destinationEnvVar: "REACT_APP_PRO_APPLICATION_URL",
  },
  {
    canonicalPath: "/laborpass",
    aliases: ["/portfolio"],
    pageContext: "laborpass",
    destinationEnvVar: "REACT_APP_PRO_APPLICATION_URL",
  },
  {
    canonicalPath: "/tools-financing",
    aliases: [],
    pageContext: "tools-financing",
    destinationEnvVar: "REACT_APP_PRO_APPLICATION_URL",
  },
  {
    canonicalPath: "/community-manager",
    aliases: ["/training"],
    pageContext: "community-manager",
    destinationEnvVar: "REACT_APP_TRAINING_URL",
  },
  {
    canonicalPath: "/businesses",
    aliases: ["/business"],
    pageContext: "businesses",
    destinationEnvVar: "REACT_APP_MARKETPLACE_URL",
  },
  {
    canonicalPath: "/homeowners",
    aliases: [],
    pageContext: "homeowners",
    destinationEnvVar: "REACT_APP_HOMEOWNER_DASHBOARD_URL",
  },
];

function normalizePathname(pathname: string) {
  const [pathWithoutQuery] = pathname.split("?");
  const [normalizedPath] = pathWithoutQuery.split("#");

  if (!normalizedPath) {
    return "/";
  }

  return normalizedPath === "/"
    ? normalizedPath
    : normalizedPath.replace(/\/+$/, "");
}

function matchesBasePath(pathname: string, basePath: string) {
  return pathname === basePath || pathname.startsWith(`${basePath}/`);
}

function getFallbackDestination(
  action: NavbarAuthAction,
  pageContext: NavbarPageContext = "unknown",
): NavbarResolvedDestination {
  return {
    action,
    pageContext,
    href: WEBSITE_AUTH_FALLBACK[action],
    isExternalAppDestination: false,
  };
}

export function resolveNavbarAuthDestination(
  pathname: string,
  action: NavbarAuthAction,
  options: { isLeanServicesMode?: boolean } = {},
): NavbarResolvedDestination {
  // Normalize away query strings, hashes, and trailing slashes so route
  // detection stays stable across campaign links and in-page anchors.
  const normalizedPathname = normalizePathname(pathname);

  // The current pathname is compared against each route entry until one entry
  // claims it through its canonical path or one of its aliases.
  const matchedRoute = NAVBAR_AUTH_ROUTE_CONTEXT.find((route) =>
    // Within a single route entry, a match on either the canonical path or any
    // alias is enough to use that entry's page context and destination, so
    // `/artisans` resolves through the skilled-workers config.
    [route.canonicalPath, ...route.aliases].some((path) =>
      matchesBasePath(normalizedPathname, path),
    ),
  );

  // Resolve the app URL lazily from the current environment. If an env var is
  // missing, the caller will fall back to the website auth routes instead.
  const destination = matchedRoute
    ? process.env[matchedRoute.destinationEnvVar]
    : undefined;

  if (!matchedRoute) {
    return getFallbackDestination(action);
  }

  if (
    options.isLeanServicesMode &&
    ["community-manager", "homeowners"].includes(matchedRoute.pageContext)
  ) {
    return getFallbackDestination(action, matchedRoute.pageContext);
  }

  if (!destination) {
    return getFallbackDestination(action, matchedRoute.pageContext);
  }

  return {
    action,
    pageContext: matchedRoute.pageContext,
    href: destination,
    isExternalAppDestination: true,
  };
}
